import React, { useMemo, useState } from 'react';
import { Target, Users, Euro, Star } from 'lucide-react';
import { Progress } from './ui/progress';
import { useSubgoal } from '../hooks/useSubgoal';

const TAB_ICONS = {
  followers: Users,
  subs: Star,
  donations: Euro,
};

function formatAmount(n, unit) {
  if (n == null || Number.isNaN(n)) return '0';
  const num = Number(n);
  if (unit === 'EUR' || unit === '€') {
    return `€${num.toLocaleString('nl-NL', { maximumFractionDigits: 0 })}`;
  }
  if (Number.isInteger(num)) return num.toLocaleString('nl-NL');
  return num.toLocaleString('nl-NL', { maximumFractionDigits: 1 });
}

function formatUnitSuffix(unit) {
  if (!unit) return '';
  if (unit === 'EUR' || unit === '€') return '';
  return ` ${unit}`;
}

export default function Subgoal() {
  const { goals, loading } = useSubgoal();
  const [activeCategory, setActiveCategory] = useState('followers');

  const activeGoals = useMemo(
    () => (goals || []).filter((g) => g && g.active),
    [goals]
  );

  if (loading || activeGoals.length === 0) return null;

  const selected =
    activeGoals.find((g) => g.category === activeCategory) || activeGoals[0];

  const target = Number(selected.target_amount) || 0;
  const current = Number(selected.current_amount) || 0;
  const rawPct = target > 0 ? (current / target) * 100 : 0;
  const pct = Math.max(0, Math.min(100, rawPct));
  const reached = current >= target && target > 0;
  const unitSuffix = formatUnitSuffix(selected.unit);

  return (
    <section
      className="mt-8 max-w-xl"
      data-testid="subgoal-widget"
      aria-label="Stream doelen"
    >
      <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-fuchsia-500/10 via-violet-500/[0.06] to-orange-500/10 p-5 relative overflow-hidden">
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-fuchsia-500/20 blur-3xl pointer-events-none" />

        <div className="flex gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4 text-fuchsia-300" />
                <span
                  className="text-[10px] uppercase tracking-[0.18em] text-fuchsia-300 font-bold"
                  data-testid="subgoal-active-label"
                >
                  {selected.label}
                </span>
                {selected.category === 'donations' && selected.period === 'last_month' && (
                  <span
                    className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-fuchsia-500/15 text-fuchsia-200 border border-fuchsia-400/30"
                    data-testid="subgoal-period-badge"
                  >
                    deze maand
                  </span>
                )}
              </div>
              <span
                className="text-xs font-bold text-white tabular-nums"
                data-testid="subgoal-percentage"
              >
                {pct.toFixed(0)}%
              </span>
            </div>

            <h3
              className="text-lg sm:text-xl font-extrabold text-white leading-snug"
              data-testid="subgoal-title"
            >
              {selected.title}
            </h3>
            {selected.description && (
              <p className="text-sm text-zinc-300 mt-1">{selected.description}</p>
            )}

            <div className="mt-4">
              <Progress
                value={pct}
                className="h-3 bg-white/10 [&>div]:bg-gradient-to-r [&>div]:from-fuchsia-400 [&>div]:via-violet-400 [&>div]:to-orange-300"
                data-testid="subgoal-progress"
              />
              <div className="flex items-center justify-between mt-2 text-sm">
                <span
                  className="text-white font-semibold tabular-nums"
                  data-testid="subgoal-current"
                >
                  {formatAmount(current, selected.unit)}
                  {unitSuffix}
                </span>
                <span
                  className="text-zinc-400 tabular-nums"
                  data-testid="subgoal-target"
                >
                  doel: {formatAmount(target, selected.unit)}
                  {unitSuffix}
                </span>
              </div>
            </div>

            {reached && (
              <div
                className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 text-xs font-semibold"
                data-testid="subgoal-reached-badge"
              >
                Doel bereikt — bedankt!
              </div>
            )}
          </div>

          {activeGoals.length > 1 && (
            <div
              className="shrink-0 flex flex-col gap-1.5 border-l border-white/10 pl-3"
              role="tablist"
              aria-label="Doel categorie"
              data-testid="subgoal-tabs"
            >
              {activeGoals.map((g) => {
                const Icon = TAB_ICONS[g.category] || Target;
                const isActive =
                  (selected.category || activeCategory) === g.category;
                return (
                  <button
                    key={g.category}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setActiveCategory(g.category)}
                    data-testid={`subgoal-tab-${g.category}`}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-semibold transition-colors border ${
                      isActive
                        ? 'bg-gradient-to-r from-fuchsia-500/30 via-violet-500/20 to-orange-400/30 text-white border-fuchsia-400/40 shadow-sm shadow-fuchsia-900/30'
                        : 'bg-white/[0.03] text-zinc-300 border-white/10 hover:bg-white/[0.07] hover:text-white'
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    <span>{g.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
